// ─── White-Label Theme Presets ────────────────────────────────────────────────
// CSS variable overrides per brand, applied on top of the base definitions
// in src/assets/scss/main.scss.
//
// All variable names here MUST match what is already defined in main.scss —
// do not invent new variable names. The values here can be updated per brand
// without touching SCSS.
//
// HOW TO USE:
//   import { getThemePreset, getCSSVars } from '@/config/theme'
//   // Apply all vars for a preset + mode:
//   const vars = getCSSVars('dougjonesfor scheme-based sets use getThemePreset().
//
// APPLYING VARS:
//   Object.entries(vars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v))
//
// NOTE: The Change Network and UniMiddle colors are placeholders — update later per brand guidelines.
// ─────────────────────────────────────────────────────────────────────────────

const themes = {

  // ── Default (dougjones / current app colors from main.scss) ─────────────────
  default: {
    light: {
      // Primary interactive (buttons, tabs, accents)
      '--interactive-01':                 '#B44C6C',
      '--interactive-01-new':             '#001871',
      '--interactive-01-hover':           '#B44C6C',
      '--interactive-01-hover-new':       '#001871e0',
      '--interactive-01-active':          '#B44C6C',
      '--interactive-01-active-new':      '#001871',
      '--interactive-primary-disabled-bg':'#00187187',
      '--primary-button-bg':              '#B44C6C',

      // Text colors (brand-tinted)
      '--text-black-white':               '#001871',
      '--text-black-white-new':           '#001871',
      '--text-theme-new':                 '#001871',
      '--text-green-white':               '#001871',
      '--text-color-civic':               '#B44C6C',
      '--text-color-value-label':         '#4849A1',
      '--welcome-text':                   '#843688',
      '--subtitle-login':                 '#4849A1',
      '--member-text':                    '#4849A1',
      '--tip-text-color':                 '#646464',
      '--tip-text-color-new':             '#646464',

      // Borders and accents
      '--dote':                           '#4849A1',
      '--dote-new':                       '#646464',
      '--input-text':                     '#B44C6C',
      '--input-field-border':             '#F4E6FF',
      '--border-leftbar-transparent':     '#F4E6FF',
      '--reaction-bg':                    '#F4E6FF',
      '--border-reaction-bar':            '#F4E6FF',
      '--outline-reaction-bar':           '#F4E6FF',
      '--border-color':                   '#005D9E',
      '--border-color-new':               '#001871',

      // Tabs
      '--tab-text-color':                 '#B44C6C',
      '--tab-inactive-text-color':        '#B44C6C',
      '--active-tab-text':                '#B44C6C',
      '--active-tab-text-new':            '#001871',
      '--inactive-tab-text':              '#843688',
      '--inactive-tab-text-new':          '#628BA8',

      // Buttons & dropdowns
      '--joine-button':                   '#001871',
      '--joine-button-hover':             '#001871',
      '--post-dropdown':                  '#B44C6C',
      '--post-dropdown-new':              '#001871',
      '--edite-button':                   '#B44C6C',
      '--right-sidebar-protip':           '#B44C6C',
      '--right-sidebar-protip-new':       '#001871',
      '--dropdown-text':                  '#001871',
      '--dropdown-icon':                  '#005D9E',

      // Container heads & navigation
      '--container-head':                 '#A888A2',
      '--container-head-new':             '#001871',

      // Sidebar / groups
      '--quick-link-icon-bg':             '#001871',
      '--green-light-white':              '#001871',
      '--group-title-text':               '#001871',
      '--status-box-new':                 '#005D9E',

      // Backgrounds & layout
      '--button-border-color':            '#326295',
      '--theme-wise-background':          '#FFFFFF',
      '--theme-wise-background-new':      '#F5F6F7',
      '--login-bg':                       '#FFFFFF',
      '--new-cell-field-border':          '#326295',
      '--new-cell-field-bg':              '#fff',

      // Color scheme switcher
      '--switcher-icon-dark-color':       '#FFAD00',
      '--switcher-icon-light-color':      '#001871',

      // Notifications
      '--notification-border-unread-light-new': '#001871eb',
    },
    dark: {
      '--interactive-01':                 '#B44C6C',
      '--interactive-01-new':             '#FFAD00',
      '--interactive-01-hover':           '#B44C6C',
      '--interactive-01-hover-new':       '#FFAD00',
      '--interactive-01-active':          '#B44C6C',
      '--interactive-01-active-new':      '#FFAD00',
      '--interactive-primary-disabled-bg':'#FFAD0087',
      '--primary-button-bg':              '#B44C6C',

      '--text-black-white':               '#FFAD00',
      '--text-black-white-new':           '#FFAD00',
      '--text-theme-new':                 '#FFAD00',
      '--text-green-white':               '#fff',
      '--text-color-civic':               '#ffffff',
      '--text-color-value-label':         '#BCA3DA',
      '--welcome-text':                   '#B44C6C',
      '--subtitle-login':                 '#BCA3DA',
      '--member-text':                    '#A7AFC7',
      '--tip-text-color':                 '#FCFCFD',
      '--tip-text-color-new':             '#FCFCFD',

      '--dote':                           '#A7AFC7',
      '--dote-new':                       '#B9CAFF',
      '--input-text':                     '#A888A2',
      '--input-field-border':             '#D9E5EC',
      '--border-leftbar-transparent':     '#54555C',
      '--reaction-bg':                    '#141414',
      '--border-reaction-bar':            '#2C2C2C',
      '--outline-reaction-bar':           '#2C2C2C',
      '--border-color':                   '#7FCBD8',
      '--border-color-new':               '#7FCBD8',

      '--tab-text-color':                 '#ffffff',
      '--tab-inactive-text-color':        '#ffffff',
      '--active-tab-text':                '#B44C6C',
      '--active-tab-text-new':            '#FFAD00',
      '--inactive-tab-text':              '#843688',
      '--inactive-tab-text-new':          '#C1C1C1',

      '--joine-button':                   '#FFAD00',
      '--joine-button-hover':             '#FFAD00',
      '--post-dropdown':                  '#FFAD00',
      '--post-dropdown-new':              '#FFAD00',
      '--edite-button':                   '#A888A2',
      '--right-sidebar-protip':           '#FAFAFA',
      '--right-sidebar-protip-new':       '#FFAD00',
      '--dropdown-text':                  '#fff',
      '--dropdown-icon':                  '#000',

      '--container-head':                 '#A888A2',
      '--container-head-new':             '#FFAD00',

      '--quick-link-icon-bg':             '#FFAD00',
      '--green-light-white':              '#e4e4e7',
      '--group-title-text':               '#B9CAFF',
      '--status-box-new':                 '#FFAD00',

      // Backgrounds & layout
      '--button-border-color':            '#FFFCF1',
      '--theme-wise-background':          '#3E4049',
      '--theme-wise-background-new':      '#282A31',
      '--login-bg':                       '#141414',
      '--new-cell-field-border':          '#FFAD00',
      '--new-cell-field-bg':              '#282A31',

      // Color scheme switcher
      '--switcher-icon-dark-color':       '#FFAD00',
      '--switcher-icon-light-color':      '#001871',

      // Notifications
      '--notification-border-unread-dark-new': '#FFFCF1',
    },
  },

  // ── The Change Network ────────────────────────────────────────────────────────────
  // TODO: Replace placeholder values with actual The Change Network brand colors.
  // Current values mirror the default theme — update once brand guidelines are received.
  dougjoneslight: {
    light: {
      '--interactive-01':                 '#B44C6C',
      '--interactive-01-new':             '#001871',
      '--interactive-01-hover':           '#B44C6C',
      '--interactive-01-hover-new':       '#001871e0',
      '--interactive-01-active':          '#B44C6C',
      '--interactive-01-active-new':      '#001871',
      '--interactive-primary-disabled-bg':'#00187187',
      '--primary-button-bg':              '#B44C6C',
      '--text-black-white':               '#001871',
      '--text-black-white-new':           '#001871',
      '--text-theme-new':                 '#001871',
      '--text-green-white':               '#001871',
      '--text-color-civic':               '#B44C6C',
      '--text-color-value-label':         '#4849A1',
      '--welcome-text':                   '#843688',
      '--subtitle-login':                 '#4849A1',
      '--member-text':                    '#4849A1',
      '--tip-text-color':                 '#646464',
      '--tip-text-color-new':             '#646464',
      '--dote':                           '#4849A1',
      '--dote-new':                       '#646464',
      '--input-text':                     '#B44C6C',
      '--input-field-border':             '#F4E6FF',
      '--border-leftbar-transparent':     '#F4E6FF',
      '--reaction-bg':                    '#F4E6FF',
      '--border-reaction-bar':            '#F4E6FF',
      '--outline-reaction-bar':           '#F4E6FF',
      '--border-color':                   '#005D9E',
      '--border-color-new':               '#001871',
      '--tab-text-color':                 '#B44C6C',
      '--tab-inactive-text-color':        '#B44C6C',
      '--active-tab-text':                '#B44C6C',
      '--active-tab-text-new':            '#001871',
      '--inactive-tab-text':              '#843688',
      '--inactive-tab-text-new':          '#628BA8',
      '--joine-button':                   '#001871',
      '--joine-button-hover':             '#001871',
      '--post-dropdown':                  '#B44C6C',
      '--post-dropdown-new':              '#001871',
      '--edite-button':                   '#B44C6C',
      '--right-sidebar-protip':           '#B44C6C',
      '--right-sidebar-protip-new':       '#001871',
      '--dropdown-text':                  '#001871',
      '--dropdown-icon':                  '#005D9E',
      '--container-head':                 '#A888A2',
      '--container-head-new':             '#001871',
      '--quick-link-icon-bg':             '#001871',
      '--green-light-white':              '#001871',
      '--group-title-text':               '#001871',
      '--status-box-new':                 '#005D9E',

      // Backgrounds & layout
      '--button-border-color':            '#326295',
      '--theme-wise-background':          '#FFFFFF',
      '--theme-wise-background-new':      '#F5F6F7',
      '--login-bg':                       '#FFFFFF',
      '--new-cell-field-border':          '#326295',
      '--new-cell-field-bg':              '#fff',

      // Color scheme switcher
      '--switcher-icon-dark-color':       '#FFAD00',
      '--switcher-icon-light-color':      '#001871',

      // Notifications
      '--notification-border-unread-light-new': '#001871eb',
    },
    dark: {
      '--interactive-01':                 '#B44C6C',
      '--interactive-01-new':             '#FFAD00',
      '--interactive-01-hover':           '#B44C6C',
      '--interactive-01-hover-new':       '#FFAD00',
      '--interactive-01-active':          '#B44C6C',
      '--interactive-01-active-new':      '#FFAD00',
      '--interactive-primary-disabled-bg':'#FFAD0087',
      '--primary-button-bg':              '#B44C6C',
      '--text-black-white':               '#FFAD00',
      '--text-black-white-new':           '#FFAD00',
      '--text-theme-new':                 '#FFAD00',
      '--text-green-white':               '#fff',
      '--text-color-civic':               '#ffffff',
      '--text-color-value-label':         '#BCA3DA',
      '--welcome-text':                   '#B44C6C',
      '--subtitle-login':                 '#BCA3DA',
      '--member-text':                    '#A7AFC7',
      '--tip-text-color':                 '#FCFCFD',
      '--tip-text-color-new':             '#FCFCFD',
      '--dote':                           '#A7AFC7',
      '--dote-new':                       '#B9CAFF',
      '--input-text':                     '#A888A2',
      '--input-field-border':             '#D9E5EC',
      '--border-leftbar-transparent':     '#54555C',
      '--reaction-bg':                    '#141414',
      '--border-reaction-bar':            '#2C2C2C',
      '--outline-reaction-bar':           '#2C2C2C',
      '--border-color':                   '#7FCBD8',
      '--border-color-new':               '#7FCBD8',
      '--tab-text-color':                 '#ffffff',
      '--tab-inactive-text-color':        '#ffffff',
      '--active-tab-text':                '#B44C6C',
      '--active-tab-text-new':            '#FFAD00',
      '--inactive-tab-text':              '#843688',
      '--inactive-tab-text-new':          '#C1C1C1',
      '--joine-button':                   '#FFAD00',
      '--joine-button-hover':             '#FFAD00',
      '--post-dropdown':                  '#FFAD00',
      '--post-dropdown-new':              '#FFAD00',
      '--edite-button':                   '#A888A2',
      '--right-sidebar-protip':           '#FAFAFA',
      '--right-sidebar-protip-new':       '#FFAD00',
      '--dropdown-text':                  '#fff',
      '--dropdown-icon':                  '#000',
      '--container-head':                 '#A888A2',
      '--container-head-new':             '#FFAD00',
      '--quick-link-icon-bg':             '#FFAD00',
      '--green-light-white':              '#e4e4e7',
      '--group-title-text':               '#B9CAFF',
      '--status-box-new':                 '#FFAD00',

      // Backgrounds & layout
      '--button-border-color':            '#FFFCF1',
      '--theme-wise-background':          '#3E4049',
      '--theme-wise-background-new':      '#282A31',
      '--login-bg':                       '#141414',
      '--new-cell-field-border':          '#FFAD00',
      '--new-cell-field-bg':              '#282A31',

      // Color scheme switcher
      '--switcher-icon-dark-color':       '#FFAD00',
      '--switcher-icon-light-color':      '#001871',

      // Notifications
      '--notification-border-unread-dark-new': '#FFFCF1',
    },
  },

  // ── UniMiddle ─────────────────────────────────────────────────────────────
  // TODO: Replace placeholder values with actual UniMiddle brand colors.
  // Current values mirror the default theme — update once brand guidelines are received.
  unimiddle: {
    light: {
      '--interactive-01':                 '#B44C6C',
      '--interactive-01-new':             '#001871',
      '--interactive-01-hover':           '#B44C6C',
      '--interactive-01-hover-new':       '#001871e0',
      '--interactive-01-active':          '#B44C6C',
      '--interactive-01-active-new':      '#001871',
      '--interactive-primary-disabled-bg':'#00187187',
      '--primary-button-bg':              '#B44C6C',
      '--text-black-white':               '#001871',
      '--text-black-white-new':           '#001871',
      '--text-theme-new':                 '#001871',
      '--text-green-white':               '#001871',
      '--text-color-civic':               '#B44C6C',
      '--text-color-value-label':         '#4849A1',
      '--welcome-text':                   '#843688',
      '--subtitle-login':                 '#4849A1',
      '--member-text':                    '#4849A1',
      '--tip-text-color':                 '#646464',
      '--tip-text-color-new':             '#646464',
      '--dote':                           '#4849A1',
      '--dote-new':                       '#646464',
      '--input-text':                     '#B44C6C',
      '--input-field-border':             '#F4E6FF',
      '--border-leftbar-transparent':     '#F4E6FF',
      '--reaction-bg':                    '#F4E6FF',
      '--border-reaction-bar':            '#F4E6FF',
      '--outline-reaction-bar':           '#F4E6FF',
      '--border-color':                   '#005D9E',
      '--border-color-new':               '#001871',
      '--tab-text-color':                 '#B44C6C',
      '--tab-inactive-text-color':        '#B44C6C',
      '--active-tab-text':                '#B44C6C',
      '--active-tab-text-new':            '#001871',
      '--inactive-tab-text':              '#843688',
      '--inactive-tab-text-new':          '#628BA8',
      '--joine-button':                   '#001871',
      '--joine-button-hover':             '#001871',
      '--post-dropdown':                  '#B44C6C',
      '--post-dropdown-new':              '#001871',
      '--edite-button':                   '#B44C6C',
      '--right-sidebar-protip':           '#B44C6C',
      '--right-sidebar-protip-new':       '#001871',
      '--dropdown-text':                  '#001871',
      '--dropdown-icon':                  '#005D9E',
      '--container-head':                 '#A888A2',
      '--container-head-new':             '#001871',
      '--quick-link-icon-bg':             '#001871',
      '--green-light-white':              '#001871',
      '--group-title-text':               '#001871',
      '--status-box-new':                 '#005D9E',

      // Backgrounds & layout
      '--button-border-color':            '#326295',
      '--theme-wise-background':          '#FFFFFF',
      '--theme-wise-background-new':      '#F5F6F7',
      '--login-bg':                       '#FFFFFF',
      '--new-cell-field-border':          '#326295',
      '--new-cell-field-bg':              '#fff',

      // Color scheme switcher
      '--switcher-icon-dark-color':       '#FFAD00',
      '--switcher-icon-light-color':      '#001871',

      // Notifications
      '--notification-border-unread-light-new': '#001871eb',
    },
    dark: {
      '--interactive-01':                 '#B44C6C',
      '--interactive-01-new':             '#FFAD00',
      '--interactive-01-hover':           '#B44C6C',
      '--interactive-01-hover-new':       '#FFAD00',
      '--interactive-01-active':          '#B44C6C',
      '--interactive-01-active-new':      '#FFAD00',
      '--interactive-primary-disabled-bg':'#FFAD0087',
      '--primary-button-bg':              '#B44C6C',
      '--text-black-white':               '#FFAD00',
      '--text-black-white-new':           '#FFAD00',
      '--text-theme-new':                 '#FFAD00',
      '--text-green-white':               '#fff',
      '--text-color-civic':               '#ffffff',
      '--text-color-value-label':         '#BCA3DA',
      '--welcome-text':                   '#B44C6C',
      '--subtitle-login':                 '#4849A1',
      '--member-text':                    '#A7AFC7',
      '--tip-text-color':                 '#FCFCFD',
      '--tip-text-color-new':             '#FCFCFD',
      '--dote':                           '#A7AFC7',
      '--dote-new':                       '#B9CAFF',
      '--input-text':                     '#A888A2',
      '--input-field-border':             '#D9E5EC',
      '--border-leftbar-transparent':     '#54555C',
      '--reaction-bg':                    '#141414',
      '--border-reaction-bar':            '#2C2C2C',
      '--outline-reaction-bar':           '#2C2C2C',
      '--border-color':                   '#7FCBD8',
      '--border-color-new':               '#7FCBD8',
      '--tab-text-color':                 '#ffffff',
      '--tab-inactive-text-color':        '#ffffff',
      '--active-tab-text':                '#B44C6C',
      '--active-tab-text-new':            '#FFAD00',
      '--inactive-tab-text':              '#843688',
      '--inactive-tab-text-new':          '#C1C1C1',
      '--joine-button':                   '#FFAD00',
      '--joine-button-hover':             '#FFAD00',
      '--post-dropdown':                  '#FFAD00',
      '--post-dropdown-new':              '#FFAD00',
      '--edite-button':                   '#A888A2',
      '--right-sidebar-protip':           '#FAFAFA',
      '--right-sidebar-protip-new':       '#FFAD00',
      '--dropdown-text':                  '#fff',
      '--dropdown-icon':                  '#000',
      '--container-head':                 '#A888A2',
      '--container-head-new':             '#FFAD00',
      '--quick-link-icon-bg':             '#FFAD00',
      '--green-light-white':              '#e4e4e7',
      '--group-title-text':               '#B9CAFF',
      '--status-box-new':                 '#FFAD00',

      // Backgrounds & layout
      '--button-border-color':            '#FFFCF1',
      '--theme-wise-background':          '#3E4049',
      '--theme-wise-background-new':      '#282A31',
      '--login-bg':                       '#141414',
      '--new-cell-field-border':          '#FFAD00',
      '--new-cell-field-bg':              '#282A31',

      // Color scheme switcher
      '--switcher-icon-dark-color':       '#FFAD00',
      '--switcher-icon-light-color':      '#001871',

      // Notifications
      '--notification-border-unread-dark-new': '#FFFCF1',
    },
  },

  // ── Allen Jackson ─────────────────────────────────────────────────────────
  // Copied from launch_pad_fe/src/config/theme.js
  // Light: Primary navy #1F3B5B, Gold accent #C8A45D, BG #F5F6F8, Text #1A1A1A
  // Dark:  Primary navy #27496D,  Gold accent #D4B06A, BG #121417, Surface #1B1F24
  allenjackson: {
    light: {
      '--interactive-01':                  '#C8A45D',
      '--interactive-01-new':              '#1F3B5B',
      '--interactive-01-hover':            '#B8944D',
      '--interactive-01-hover-new':        '#1F3B5Be0',
      '--interactive-01-active':           '#C8A45D',
      '--interactive-01-active-new':       '#1F3B5B',
      '--interactive-primary-disabled-bg': '#C8A45D87',
      '--primary-button-bg':               '#C8A45D',

      '--text-black-white':                '#1F3B5B',
      '--text-black-white-new':            '#1F3B5B',
      '--text-theme-new':                  '#1F3B5B',
      '--text-green-white':                '#1F3B5B',
      '--text-color-civic':                '#1F3B5B',
      '--text-color-value-label':          '#6B7280',
      '--welcome-text':                    '#1F3B5B',
      '--subtitle-login':                  '#6B7280',
      '--member-text':                     '#6B7280',
      '--tip-text-color':                  '#6B7280',
      '--tip-text-color-new':              '#6B7280',

      '--dote':                            '#1F3B5B',
      '--dote-new':                        '#6B7280',
      '--input-text':                      '#1F3B5B',
      '--input-field-border':              '#E2E5EA',
      '--border-leftbar-transparent':      '#E2E5EA',
      '--reaction-bg':                     '#F5F6F8',
      '--border-reaction-bar':             '#E2E5EA',
      '--outline-reaction-bar':            '#E2E5EA',
      '--border-color':                    '#1F3B5B',
      '--border-color-new':                '#1F3B5B',

      '--tab-text-color':                  '#1F3B5B',
      '--tab-inactive-text-color':         '#1F3B5B',
      '--active-tab-text':                 '#1F3B5B',
      '--active-tab-text-new':             '#1F3B5B',
      '--inactive-tab-text':               '#6B7280',
      '--inactive-tab-text-new':           '#6B7280',

      '--joine-button':                    '#1F3B5B',
      '--joine-button-hover':              '#1F3B5B',
      '--post-dropdown':                   '#1F3B5B',
      '--post-dropdown-new':               '#1F3B5B',
      '--edite-button':                    '#1F3B5B',
      '--right-sidebar-protip':            '#1F3B5B',
      '--right-sidebar-protip-new':        '#1F3B5B',
      '--dropdown-text':                   '#1F3B5B',
      '--dropdown-icon':                   '#1F3B5B',

      '--container-head':                  '#1F3B5B',
      '--container-head-new':              '#1F3B5B',
      '--quick-link-icon-bg':              '#1F3B5B',
      '--green-light-white':               '#1F3B5B',
      '--group-title-text':                '#1F3B5B',
      '--status-box-new':                  '#C8A45D',

      '--button-border-color':             '#1F3B5B',
      '--theme-wise-background':           '#FFFFFF',
      '--theme-wise-background-new':       '#fff',
      '--login-bg':                        '#FFFFFF',
      '--new-cell-field-border':           '#1F3B5B',
      '--new-cell-field-bg':               '#FFFFFF',

      '--switcher-icon-dark-color':        '#D4B06A',
      '--switcher-icon-light-color':       '#1F3B5B',

      '--notification-border-unread-light-new': '#1F3B5Beb',
      '--header-border-light-new': '#1F3B5B',
      '--border-leftbar-component': '#1F3B5B',
      '--header-bg-light-new': '#F5F6F8',
    },
    dark: {
      '--interactive-01':                  '#D4B06A',
      '--interactive-01-new':              '#D4B06A',
      '--interactive-01-hover':            '#D4B06A',
      '--interactive-01-hover-new':        '#D4B06A',
      '--interactive-01-active':           '#D4B06A',
      '--interactive-01-active-new':       '#D4B06A',
      '--interactive-primary-disabled-bg': '#D4B06A87',
      '--primary-button-bg':               '#D4B06A',

      '--text-black-white':                '#D4B06A',
      '--text-black-white-new':            '#D4B06A',
      '--text-theme-new':                  '#D4B06A',
      '--text-green-white':                '#F5F5F5',
      '--text-color-civic':                '#F5F5F5',
      '--text-color-value-label':          '#A1A1AA',
      '--welcome-text':                    '#D4B06A',
      '--subtitle-login':                  '#A1A1AA',
      '--member-text':                     '#A1A1AA',
      '--tip-text-color':                  '#F5F5F5',
      '--tip-text-color-new':              '#F5F5F5',

      '--dote':                            '#D4B06A',
      '--dote-new':                        '#A1A1AA',
      '--input-text':                      '#D4B06A',
      '--input-field-border':              '#2B3138',
      '--border-leftbar-transparent':      '#2B3138',
      '--reaction-bg':                     '#121417',
      '--border-reaction-bar':             '#2B3138',
      '--outline-reaction-bar':            '#2B3138',
      '--border-color':                    '#D4B06A',
      '--border-color-new':                '#D4B06A',

      '--tab-text-color':                  '#F5F5F5',
      '--tab-inactive-text-color':         '#F5F5F5',
      '--active-tab-text':                 '#D4B06A',
      '--active-tab-text-new':             '#D4B06A',
      '--inactive-tab-text':               '#A1A1AA',
      '--inactive-tab-text-new':           '#A1A1AA',

      '--joine-button':                    '#D4B06A',
      '--joine-button-hover':              '#D4B06A',
      '--post-dropdown':                   '#D4B06A',
      '--post-dropdown-new':               '#D4B06A',
      '--edite-button':                    '#D4B06A',
      '--right-sidebar-protip':            '#F5F5F5',
      '--right-sidebar-protip-new':        '#D4B06A',
      '--dropdown-text':                   '#F5F5F5',
      '--dropdown-icon':                   '#F5F5F5',

      '--container-head':                  '#D4B06A',
      '--container-head-new':              '#D4B06A',
      '--quick-link-icon-bg':              '#D4B06A',
      '--green-light-white':               '#A1A1AA',
      '--group-title-text':                '#D4B06A',
      '--status-box-new':                  '#D4B06A',

      '--button-border-color':             '#D4B06A',
      '--theme-wise-background':           '#1B1F24',
      '--theme-wise-background-new':       '#121417',
      '--login-bg':                        '#121417',
      '--new-cell-field-border':           '#D4B06A',
      '--new-cell-field-bg':               '#1B1F24',

      '--switcher-icon-dark-color':        '#D4B06A',
      '--switcher-icon-light-color':       '#27496D',

      '--notification-border-unread-dark-new': '#D4B06A',
      '--header-border-light-new': '#D4B06A',
      '--border-leftbar-component': '#D4B06A',
      '--header-bg-light-new': '#121417',
    },
  },
};

export default themes;

const defaultLoginTheme = {
  light: {
    '--login-card-bg': '#F8F9FD',
    '--login-card-border': '#326295',
    '--login-welcom-text': '#001871',
    '--login-header-text': '#001871',
    '--login-button-bg': '#001871',
    '--login-button-border': '#FFFFFF',
    '--login-password-icon': '#000000',
    '--login-input-border': '#001871',
    '--login-dropdown-bg': '#F8F9FD',
    '--login-dropdown-border': '#A1A1AA',
  },
  dark: {
    '--login-card-bg': '#3E4049',
    '--login-card-border': '#848484',
    '--login-welcom-text': '#FFFFFF',
    '--login-header-text': '#D1E9FF',
    '--login-button-bg': '#FFAD00',
    '--login-button-border': '#000000',
    '--login-password-icon': '#FFFFFF',
    '--login-input-border': '#D1E9FF',
    '--login-dropdown-bg': '#282A31',
    '--login-dropdown-border': '#D1E9FF',
  },
};

const loginThemes = {
  default: defaultLoginTheme,
  dougjoneslight: defaultLoginTheme,
  unimiddle: defaultLoginTheme,
  allenjackson: {
    light: {
      '--login-card-bg': '#F8F9FD',
      '--login-card-border': '#1F3B5B',
      '--login-welcom-text': '#1F3B5B',
      '--login-header-text': '#1F3B5B',
      '--login-button-bg': '#1F3B5B',
      '--login-button-border': '#FFFFFF',
      '--login-password-icon': '#1F3B5B',
      '--login-input-border': '#1F3B5B',
      '--login-dropdown-bg': '#F5F6F8',
      '--login-dropdown-border': '#6B7280',
    },
    dark: {
      '--login-card-bg': '#3E4049',
      '--login-card-border': '#2B3138',
      '--login-welcom-text': '#F5F5F5',
      '--login-header-text': '#D4B06A',
      '--login-button-bg': '#D4B06A',
      '--login-button-border': '#121417',
      '--login-password-icon': '#F5F5F5',
      '--login-input-border': '#D4B06A',
      '--login-dropdown-bg': '#121417',
      '--login-dropdown-border': '#D4B06A',
    },
  },
};

// Returns the full preset object { light: {}, dark: {} } for a brand name.
export const getThemePreset = (presetName) => themes[presetName] ?? themes['default'];

// Returns a flat CSS-var map for the given preset name + color scheme.
// Pass the result to document.documentElement.style.setProperty() calls.
export const getCSSVars = (presetName, mode = 'light') =>
  ({
    ...(getThemePreset(presetName)[mode] ?? {}),
    ...((loginThemes[presetName] || loginThemes.default)[mode] ?? {}),
  });
